import { useState, useEffect, useRef, useCallback } from "react";
import { api, API } from "@/lib/api";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { UploadSimple, FilePdf, Image as ImageIcon, Trash, ArrowSquareOut } from "@phosphor-icons/react";

export default function BoatDocumentsDialog({ boatId, boatName, open, onOpenChange }) {
  const [docs, setDocs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const inputRef = useRef(null);

  const load = useCallback(async () => {
    if (!boatId) return;
    setLoading(true);
    try { setDocs((await api.get(`/boats/${boatId}/documents`)).data); }
    catch { /* ignore */ }
    finally { setLoading(false); }
  }, [boatId]);
  useEffect(() => { if (open) load(); }, [open, load]);

  const onFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    setUploading(true);
    try {
      await api.post(`/boats/${boatId}/documents`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Το έγγραφο ανέβηκε");
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Αποτυχία ανεβάσματος");
    } finally {
      setUploading(false);
      if (inputRef.current) inputRef.current.value = "";
    }
  };

  const view = (d) => {
    const token = localStorage.getItem("token");
    window.open(`${API}/boats/${boatId}/documents/${d.id}/download?token=${token}`, "_blank");
  };
  const del = async (d) => {
    try { await api.delete(`/boats/${boatId}/documents/${d.id}`); toast.success("Το έγγραφο διαγράφηκε"); load(); }
    catch { toast.error("Σφάλμα διαγραφής"); }
  };

  const isImg = (ct) => (ct || "").startsWith("image/");

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg rounded-sm" data-testid="boat-documents-dialog">
        <DialogHeader>
          <DialogTitle>Έγγραφα σκάφους — {boatName}</DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <div>
            <input ref={inputRef} type="file" accept="image/*,application/pdf" className="hidden" onChange={onFile} data-testid="boat-doc-input" />
            <Button onClick={() => inputRef.current?.click()} disabled={uploading} className="rounded-sm bg-[#0055FF] hover:bg-[#0044CC]" data-testid="boat-doc-upload-btn">
              <UploadSimple size={16} weight="bold" /> {uploading ? "Ανέβασμα…" : "Ανέβασμα εγγράφου (φωτο / PDF)"}
            </Button>
            <p className="mt-1.5 text-[11px] text-muted-foreground">Επιτρέπονται εικόνες (jpg, png, webp) και PDF, έως 15MB.</p>
          </div>
          {loading ? (
            <div className="text-sm text-muted-foreground">Φόρτωση…</div>
          ) : docs.length === 0 ? (
            <div className="rounded-sm border border-dashed border-border p-6 text-center text-sm text-muted-foreground" data-testid="boat-docs-empty">
              Δεν υπάρχουν έγγραφα για αυτό το σκάφος.
            </div>
          ) : (
            <ul className="divide-y divide-border rounded-sm border border-border">
              {docs.map((d) => (
                <li key={d.id} className="flex items-center justify-between gap-3 px-3 py-2" data-testid={`boat-doc-${d.id}`}>
                  <div className="flex min-w-0 items-center gap-2">
                    {isImg(d.content_type) ? <ImageIcon size={18} className="text-[#0055FF]" /> : <FilePdf size={18} className="text-[#B91C1C]" />}
                    <span className="truncate text-sm">{d.original_filename}</span>
                  </div>
                  <div className="flex shrink-0 gap-1">
                    <Button variant="ghost" size="icon" className="h-8 w-8 rounded-sm" onClick={() => view(d)} data-testid={`boat-doc-view-${d.id}`}><ArrowSquareOut size={16} /></Button>
                    <Button variant="ghost" size="icon" className="h-8 w-8 rounded-sm text-destructive" onClick={() => del(d)} data-testid={`boat-doc-delete-${d.id}`}><Trash size={16} /></Button>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
